import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import apiFetch from '../../../utils/apiFetch';
import { FOG_BYTES, FOG_CELL_LIMIT, paintFogStroke } from '../../../shared/fogMask.mjs';

const decode = text => {
  const raw = atob(text);
  if (raw.length !== FOG_BYTES) throw new Error('Invalid fog snapshot.');
  return Uint8Array.from(raw, char => char.charCodeAt(0));
};
const brushKind = kind => kind === 'reveal' || kind === 'cells';
const id = () => window.crypto?.randomUUID?.() || `fog-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function useMapFog(campaignId, map, dmView = false) {
  const [view, setView] = useState({ mask: null, canEdit: false, ready: false, status: 'Loading fog…' });
  const controller = useRef(null);
  const mapId = map?.mapId;
  const imageKey = JSON.stringify([map?.imageUrl, map?.imageBase64, map?.cloudinaryPublicId, map?.width, map?.height]);
  const identity = JSON.stringify([campaignId, mapId, imageKey, dmView]);
  useEffect(() => {
    if (!campaignId || !mapId) { controller.current = null; return undefined; }
    let alive = true, closing = false, current = null, pending = [], inFlight = null, posting = false, failed = false;
    let refreshing = null, repeatRead = false, timer;
    let canEdit = false, canUndo = false, recovered = false;
    const owner = id();
    const storageKey = `realmtracker:fog-outbox:${encodeURIComponent(campaignId)}:${encodeURIComponent(mapId)}`;
    const remember = () => {
      try {
        if (canEdit && current && (inFlight || pending.length)) sessionStorage.setItem(storageKey, JSON.stringify({ owner, epoch: current.epoch, inFlight, pending }));
        else if (JSON.parse(sessionStorage.getItem(storageKey) || 'null')?.owner === owner) sessionStorage.removeItem(storageKey);
      } catch (_) { /* Storage may be unavailable; the visible save indicator remains authoritative. */ }
    };
    const url = `/campaigns/${encodeURIComponent(campaignId)}/maps/${encodeURIComponent(mapId)}/fog`;
    const publish = (status = failed ? 'Save failed — retry' : pending.length || inFlight ? 'Saving…' : 'Saved') => {
      remember();
      if (!alive) return;
      let mask = current?.mask || null;
      if (mask && canEdit && (inFlight || pending.length)) {
        mask = mask.slice();
        if (brushKind(inFlight?.kind)) paintFogStroke(mask, inFlight);
        pending.forEach(stroke => paintFogStroke(mask, stroke));
      }
      setView({ identity, mask, canEdit, canUndo, ready: Boolean(current), status });
    };
    const accept = data => {
      if (data.mapId !== mapId || !Number.isSafeInteger(data.revision)) return;
      if (current && data.revision < current.revision) return;
      if (typeof data.canUndo === 'boolean') canUndo = data.canUndo;
      if (data.mask) {
        if (current && data.epoch !== current.epoch) { pending = []; inFlight = null; failed = false; }
        current = { mask: decode(data.mask), epoch: data.epoch, revision: data.revision };
      } else {
        if (!current || current.epoch !== data.epoch || data.revision > current.revision + 1) { refresh(); return; }
        if (data.revision <= current.revision) return;
        const mask = current.mask.slice();
        for (const [offset, value] of data.changes || []) {
          if (!Number.isInteger(offset) || offset < 0 || offset >= mask.length || !Number.isInteger(value) || value < 0 || value > 255) { refresh(); return; }
          mask[offset] = value;
        }
        current = { mask, epoch: data.epoch, revision: data.revision };
      }
      publish();
    };
    const refresh = () => {
      if (refreshing) { repeatRead = true; return refreshing; }
      refreshing = (async () => {
        do {
          repeatRead = false;
          try {
            const response = await apiFetch(url);
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Fog unavailable.');
            if (alive || closing) {
              canEdit = dmView && data.canEdit === true; accept(data);
              if (!recovered && canEdit && current) {
                recovered = true;
                try {
                  const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
                  if (saved?.epoch === current.epoch && Array.isArray(saved.pending) && saved.pending.length <= 129) {
                    pending = saved.pending; inFlight = saved.inFlight; failed = Boolean(inFlight || pending.length);
                    publish(failed ? 'Unsaved brush strokes — retry' : undefined);
                  } else if (saved) sessionStorage.removeItem(storageKey);
                } catch (_) { /* Ignore unavailable/corrupt local recovery data. */ }
              }
            }
          } catch (error) { if (alive) publish(current ? undefined : 'Fog unavailable — retry'); }
        } while (alive && repeatRead);
      })().finally(() => { refreshing = null; });
      return refreshing;
    };
    const flush = async () => {
      if ((!alive && !closing) || posting || failed || !current || !canEdit || (!inFlight && !pending.length)) return;
      if (!inFlight) inFlight = { ...pending.shift(), id: id(), epoch: current.epoch, revision: current.revision };
      const sent = inFlight; posting = true; publish();
      try {
        const response = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sent) });
        const data = await response.json();
        if (!response.ok) {
          if (response.status === 409) {
            await refresh();
            const { id: _id, epoch: _epoch, revision: _revision, ...stroke } = sent;
            // Squares painted on an outdated grid would land in the wrong place: drop them.
            if (data.code === 'FOG_GRID_CHANGED') pending = pending.filter(entry => entry.kind !== 'cells');
            else if (current?.epoch === sent.epoch && brushKind(sent.kind)) pending.unshift(stroke);
            else pending = []; // Reset/image changes invalidate old painting.
            inFlight = null;
          }
          throw new Error(data.message || 'Could not save fog.');
        }
        accept(data); inFlight = null; failed = false; publish();
      } catch (error) { failed = true; publish(error.message + ' — retry'); }
      finally { posting = false; if (closing && !failed && pending.length) flush(); }
    };
    const control = {
      paint: stroke => {
        if (!alive || !current || !canEdit || failed || pending.length > 128) return false;
        const last = pending[pending.length - 1];
        if (stroke.kind === 'cells') {
          // One drag's squares travel together, up to the server's per-request limit.
          if (last?.kind === 'cells' && last.strokeId === stroke.strokeId && last.hide === stroke.hide && last.columns === stroke.columns
            && last.rows === stroke.rows && last.cells.length + stroke.cells.length <= FOG_CELL_LIMIT) last.cells.push(...stroke.cells);
          else pending.push({ ...stroke, cells: stroke.cells.slice() });
        } else if (stroke.points.length > 1 && last?.kind === 'reveal' && last.strokeId === stroke.strokeId && last.rx === stroke.rx && last.ry === stroke.ry && last.points.length < 32 && JSON.stringify(last.points[last.points.length - 1]) === JSON.stringify(stroke.points[0])) last.points.push(...stroke.points.slice(1));
        else pending.push({ kind: 'reveal', ...stroke });
        publish(); return true;
      },
      flush,
      retry: () => { failed = false; refresh().then(flush); },
      reset: kind => {
        if (posting || inFlight || pending.length || !current || !canEdit) return;
        inFlight = { kind, id: id(), epoch: current.epoch, revision: current.revision }; failed = false; flush();
      },
    };
    controller.current = control;
    publish('Loading fog…');
    const socket = io(process.env.REACT_APP_API_URL || undefined, { withCredentials: true });
    const join = () => socket.emit('campaign:join', campaignId, result => { if (result?.status === 'joined') refresh(); });
    const update = event => { if (event?.mapId === mapId) { try { accept(event); } catch (_) { refresh(); } } };
    socket.on('connect', join); socket.on('campaign:fog:update', update); join(); refresh();
    timer = setInterval(flush, 100);
    const recovery = setInterval(refresh, 15000);
    const focus = () => { if (!document.hidden) refresh(); };
    const beforeUnload = event => {
      if (!inFlight && !pending.length) return;
      flush(); event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', focus);
    return () => {
      // Drain queued chunks in this map controller; late responses never update another map.
      closing = true; flush(); alive = false; clearInterval(timer); clearInterval(recovery);
      socket.off('connect', join); socket.off('campaign:fog:update', update);
      socket.emit('campaign:leave', campaignId); socket.disconnect();
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus);
      if (controller.current === control) controller.current = null;
    };
  }, [campaignId, mapId, imageKey, identity, dmView]);
  const active = Boolean(campaignId && mapId);
  const currentView = view.identity === identity ? view : { mask: null, canEdit: false, ready: false, status: 'Loading fog…' };
  return { ...currentView, active, paint: stroke => controller.current?.paint(stroke),
    flush: () => controller.current?.flush(), retry: () => controller.current?.retry(), reset: kind => controller.current?.reset(kind) };
}
